import KeyboardArrowLeftRoundedIcon from '@mui/icons-material/KeyboardArrowLeftRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Image from 'next/image';
import { TransitionEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type ImagePreviewSlide = {
  src: string;
  alt: string;
};

type ImagePreviewCarouselProps = {
  slides: ImagePreviewSlide[];
  label: string;
};

const imageWrapSx = {
  position: 'relative',
  width: '100%',
  paddingBottom: '62%',
  borderRadius: '14px',
  overflow: 'hidden',
  backgroundColor: '#D6DAE2',
} as const;

const trackSx = (activeIndex: number, transitionEnabled: boolean) => ({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  transform: `translateX(-${activeIndex * 100}%)`,
  transition: transitionEnabled
    ? 'transform 760ms cubic-bezier(0.22, 1, 0.36, 1)'
    : 'none',
});

const slideSx = {
  position: 'relative',
  minWidth: '100%',
  height: '100%',
} as const;

const navButtonSx = (position: 'left' | 'right') => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  [position]: 10,
  width: 34,
  height: 34,
  backgroundColor: 'rgba(255, 255, 255, 0.78)',
  color: '#0A2440',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.92)',
  },
});

const dotsWrapSx = {
  position: 'absolute',
  left: '50%',
  bottom: 10,
  transform: 'translateX(-50%)',
  display: 'flex',
  gap: '8px',
  padding: 0,
} as const;

const dotSx = (active: boolean) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  border: 'none',
  backgroundColor: active ? '#B06A1E' : '#E7ECF4',
  cursor: 'pointer',
  transition: 'all 220ms ease',
});

const ImagePreviewCarousel = ({ slides, label }: ImagePreviewCarouselProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [virtualIndex, setVirtualIndex] = useState(1);
  const [transitionEnabled, setTransitionEnabled] = useState(true);
  const transitionLockRef = useRef(false);
  const hasMultipleSlides = slides.length > 1;

  const renderSlides = useMemo(() => {
    if (!hasMultipleSlides) return slides;
    const firstSlide = slides[0];
    const lastSlide = slides[slides.length - 1];
    return [lastSlide, ...slides, firstSlide];
  }, [hasMultipleSlides, slides]);

  const moveToNext = useCallback(() => {
    if (!hasMultipleSlides) return;
    if (transitionLockRef.current) return;
    transitionLockRef.current = true;
    setTransitionEnabled(true);
    setVirtualIndex(prev => prev + 1);
    setActiveIndex(prev => (prev + 1) % slides.length);
  }, [hasMultipleSlides, slides.length]);

  const moveToPrev = useCallback(() => {
    if (!hasMultipleSlides) return;
    if (transitionLockRef.current) return;
    transitionLockRef.current = true;
    setTransitionEnabled(true);
    setVirtualIndex(prev => prev - 1);
    setActiveIndex(prev => (prev - 1 + slides.length) % slides.length);
  }, [hasMultipleSlides, slides.length]);

  useEffect(() => {
    setActiveIndex(0);
    setVirtualIndex(1);
    setTransitionEnabled(true);
    transitionLockRef.current = false;
  }, [slides.length]);

  useEffect(() => {
    if (transitionEnabled) return;
    const firstFrame = requestAnimationFrame(() => {
      const secondFrame = requestAnimationFrame(() => {
        setTransitionEnabled(true);
      });
      return () => cancelAnimationFrame(secondFrame);
    });
    return () => cancelAnimationFrame(firstFrame);
  }, [transitionEnabled]);

  const handleTrackTransitionEnd = (event: TransitionEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget || event.propertyName !== 'transform') {
      return;
    }
    if (!hasMultipleSlides) return;

    if (virtualIndex >= slides.length + 1) {
      setTransitionEnabled(false);
      setVirtualIndex(1);
      setActiveIndex(0);
      transitionLockRef.current = false;
      return;
    }

    if (virtualIndex <= 0) {
      setTransitionEnabled(false);
      setVirtualIndex(slides.length);
      setActiveIndex(slides.length - 1);
      transitionLockRef.current = false;
      return;
    }

    transitionLockRef.current = false;
  };

  const handleDotClick = (nextIndex: number) => {
    if (!hasMultipleSlides) return;
    if (nextIndex === activeIndex) return;
    if (transitionLockRef.current) return;
    transitionLockRef.current = true;
    setTransitionEnabled(true);
    setActiveIndex(nextIndex);
    setVirtualIndex(nextIndex + 1);
  };

  return (
    <Box sx={imageWrapSx}>
      <Box
        sx={trackSx(hasMultipleSlides ? virtualIndex : 0, transitionEnabled)}
        onTransitionEnd={handleTrackTransitionEnd}
      >
        {renderSlides.map((slide, index) => (
          <Box key={`${slide.src}-${index}`} sx={slideSx}>
            <Image
              src={slide.src}
              alt={slide.alt || label}
              fill
              sizes="(max-width: 900px) 100vw, 50vw"
              style={{ objectFit: 'cover' }}
            />
          </Box>
        ))}
      </Box>

      {hasMultipleSlides && (
        <>
          <IconButton
            aria-label={`Previous ${label} image`}
            sx={navButtonSx('left')}
            onClick={moveToPrev}
          >
            <KeyboardArrowLeftRoundedIcon />
          </IconButton>
          <IconButton
            aria-label={`Next ${label} image`}
            sx={navButtonSx('right')}
            onClick={moveToNext}
          >
            <KeyboardArrowRightRoundedIcon />
          </IconButton>
          <Box sx={dotsWrapSx}>
            {slides.map((slide, index) => (
              <Box
                key={`${slide.src}-dot-${index}`}
                component="button"
                type="button"
                aria-label={`${label} image ${index + 1}`}
                sx={dotSx(index === activeIndex)}
                onClick={() => handleDotClick(index)}
              />
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

export default ImagePreviewCarousel;
